import { ConsoleBuffer } from './console-buffer';
import { NetworkBuffer } from './network-buffer';
import { BreadcrumbBuffer } from './breadcrumb-buffer';
import { ErrorListener } from './error-listener';
import { WebVitalsCollector } from './web-vitals';
import { captureScreenshot, type ScreenshotOptions, type ScreenshotResult } from './screenshot';
import { startRecording as startSessionRecording, type SessionRecordingConfig, type SessionRecordingInstance } from './session-recorder';
import type { CaptureSnapshot, BrowserInfo } from './types';

export interface BugReportCaptureConfig {
  consoleBufferSize?: number;
  breadcrumbsBufferSize?: number;
  networkBufferSize?: number;
  captureNetworkBodies?: boolean;
  smartSampling?: boolean;
}

export interface CaptureInstance {
  getSnapshot(): CaptureSnapshot;
  getSessionId(): string;
  getLastRequestIds(): string[];
  addNavigation(route: string): void;
  captureScreenshot(options?: ScreenshotOptions): Promise<ScreenshotResult>;
  startRecording(config?: SessionRecordingConfig): SessionRecordingInstance;
  destroy(): void;
}

let instance: CaptureInstance | null = null;

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;
  let family: BrowserInfo['family'] = 'other';
  let version = '';

  if (ua.includes('Edg/')) {
    family = 'edge';
    version = ua.match(/Edg\/([\d.]+)/)?.[1] ?? '';
  } else if (ua.includes('Chrome/')) {
    family = 'chrome';
    version = ua.match(/Chrome\/([\d.]+)/)?.[1] ?? '';
  } else if (ua.includes('Firefox/')) {
    family = 'firefox';
    version = ua.match(/Firefox\/([\d.]+)/)?.[1] ?? '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    family = 'safari';
    version = ua.match(/Version\/([\d.]+)/)?.[1] ?? '';
  }

  let os_family: BrowserInfo['os_family'] = 'other';
  if (/iPhone|iPad|iPod/.test(ua)) os_family = 'ios';
  else if (/Android/.test(ua)) os_family = 'android';
  else if (/Mac OS/.test(ua)) os_family = 'mac';
  else if (/Windows/.test(ua)) os_family = 'windows';
  else if (/Linux/.test(ua)) os_family = 'linux';

  return { ua, family, version, os_family };
}

export function initCapture(config?: BugReportCaptureConfig): CaptureInstance {
  if (instance) return instance;

  const sessionId = generateSessionId();
  const startedAt = new Date().toISOString();

  const consoleBuffer = new ConsoleBuffer(
    config?.consoleBufferSize ?? 200,
    config?.smartSampling ?? true,
  );
  const networkBuffer = new NetworkBuffer(config?.networkBufferSize ?? 50);
  const breadcrumbBuffer = new BreadcrumbBuffer(config?.breadcrumbsBufferSize ?? 50);
  const errorListener = new ErrorListener();
  const webVitals = new WebVitalsCollector();

  const browserInfo = detectBrowser();

  consoleBuffer.install();
  networkBuffer.install();
  breadcrumbBuffer.install();
  errorListener.install(consoleBuffer);
  webVitals.install();

  instance = {
    getSnapshot(): CaptureSnapshot {
      const networkEntries = networkBuffer.getAll();
      const lastRequestIds = networkEntries
        .filter((e) => e.request_id)
        .slice(-20)
        .map((e) => e.request_id!);

      return {
        console: consoleBuffer.getAll(),
        network: networkEntries,
        breadcrumbs: breadcrumbBuffer.getAll(),
        performance: webVitals.getData(),
        session: {
          id: sessionId,
          started_at: startedAt,
          last_request_ids: lastRequestIds,
        },
        browser: browserInfo,
        app: {
          version: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
          env: import.meta.env.MODE ?? 'production',
          url: window.location.href,
          route: window.location.pathname,
        },
      };
    },

    getSessionId(): string {
      return sessionId;
    },

    getLastRequestIds(): string[] {
      return networkBuffer
        .getAll()
        .filter((e) => e.request_id)
        .slice(-20)
        .map((e) => e.request_id!);
    },

    addNavigation(route: string): void {
      breadcrumbBuffer.addNavigation(route);
    },

    async captureScreenshot(options?: ScreenshotOptions): Promise<ScreenshotResult> {
      return captureScreenshot(options);
    },

    startRecording(config?: SessionRecordingConfig): SessionRecordingInstance {
      return startSessionRecording(config);
    },

    destroy(): void {
      consoleBuffer.destroy();
      networkBuffer.destroy();
      breadcrumbBuffer.destroy();
      errorListener.destroy();
      webVitals.destroy();
      instance = null;
    },
  };

  return instance;
}

export function getCapture(): CaptureInstance | null {
  return instance;
}

export type { CaptureSnapshot, BugReportCaptureConfig as CaptureConfig };
export type { ScreenshotOptions, ScreenshotResult } from './screenshot';
export type { SessionRecordingConfig, SessionRecordingInstance, SessionRecording, RecordedEvent } from './session-recorder';
